import { exactInteger } from '@/lib/exact'
import type { Block, Cell, ChipCell, Column, KvItem, TableBlock } from '@/kova/spec/types'

import { ApiError } from '@/lib/api/errors'

import type { ApiClient } from '@/lib/api'

import type { DataSource, ScreenData, ScreenLoader } from './provenance'
import { readAtStamp } from './provenance'
import type { Caller } from './handles'
import { contactStatusForm } from './supportcontrols'
import { callerBeside } from './writes'

/**
 * Support › Ticket context (`sp-ticket`), read from
 * `GET /v1/admin/support/contacts` (go-wallet handler
 * `internal/server/admin_support.go`).
 *
 * # A ticket nobody can see is a customer who was told a person would look
 *
 * The endpoint says it plainly: this read is what makes the human path real.
 * The app's contact-support flow files a request and tells the customer somebody
 * will look at it; without a queue anybody can see, that is the same dead end
 * the deflection funnel exists to prevent, arriving days later instead of
 * immediately.
 *
 * # The queue is worked oldest-first, and this console must not re-sort it
 *
 * The backend returns oldest-first within a status, because a support queue
 * worked newest-first is one where the oldest request is never reached. Nothing
 * here re-orders the rows.
 *
 * # `entry_route` is the difference between a choice and a failure
 *
 * Four routes, and collapsing them loses the thing the funnel is measured on:
 *
 *   - `after_guides` — they read the guides and still wanted a person.
 *   - `no_guides_available` — the funnel had nothing for their situation.
 *   - `guides_unavailable` — the guides could not be loaded. **An outage**, and
 *     counting it as the second would make a broken help centre look like a
 *     content gap for as long as it lasted.
 *   - `direct` — they went straight to a person.
 *
 * # `context_unread` is read BEFORE anything else in the context
 *
 * `verification_tier` 0 is a REAL tier — the one every new customer is in — so
 * a failed customer read is indistinguishable from an unverified customer
 * without this flag, and an agent would handle a verified business as an
 * unverified one. When it is set, every other field of the context is absent
 * rather than empty, and this view says so instead of drawing a tier.
 *
 * # `redacted_at` means the customer's words are gone, and the tombstone is ours
 *
 * An Art. 17 erasure takes the message. What is left in `message` was written
 * by us, not by the customer, and an agent reading it as theirs would be
 * answering something nobody said. It is stated on the row.
 *
 * # `claimed_refs` are claims, and this console renders them as claims
 *
 * They are shape-checked, deduped and capped, and **nothing verifies that one
 * names a movement, or a movement belonging to this customer**. The queue shows
 * them as what the customer asserted, never as attached records — an agent who
 * reads a claimed reference as a resolved one is looking at somebody else's
 * transaction.
 *
 * # Read-only
 *
 * `POST /support/contacts/{reference}/status` picks a contact up and resolves
 * it, forward only and attributed, under a permission separate from reading the
 * queue. It is not called here: marking a request handled attributes a named
 * agent to a customer's request, which is a control that belongs in a change
 * that can argue for it.
 */

export interface SupportContactView {
  readonly contact_reference: string
  readonly customer_id: number
  readonly funnel_reference?: string
  readonly entry_route: string
  readonly lang: string
  readonly message: string
  readonly redacted_at?: string
  readonly context?: Record<string, unknown>
  readonly status: string
  readonly handled_by?: string
  readonly handled_at?: string
  readonly created_at: string
}

export interface SupportContactsResponse {
  readonly contacts: readonly unknown[]
}

export const SUPPORT_CONTACTS_PATH = '/support/contacts'

const SOURCE = 'postgres · support_contacts (GET /v1/admin/support/contacts)'
const AUTHORITY = 'operational'

function text(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

function whole(value: unknown): number | null {
  return exactInteger(value)
}

function stringList(value: unknown): readonly string[] {
  if (!Array.isArray(value)) return []
  return value.filter((v): v is string => typeof v === 'string' && v !== '')
}

interface ContactContext {
  /** True when the customer read FAILED, so everything else here is absent. */
  readonly unread: boolean
  readonly accountState: string
  readonly acceptance: string
  readonly disabled: boolean | null
  readonly customerKind: string
  readonly country: string
  readonly tier: number | null
  readonly screen: string
  readonly errorCode: string
  readonly intentKey: string
  readonly offeredArticles: readonly string[]
  readonly claimedRefs: readonly string[]
}

function readContext(raw: unknown): ContactContext {
  const c = typeof raw === 'object' && raw !== null && !Array.isArray(raw)
    ? (raw as Record<string, unknown>)
    : {}
  return {
    unread: c['context_unread'] === true,
    accountState: text(c['account_state']),
    acceptance: text(c['acceptance']),
    disabled: typeof c['disabled'] === 'boolean' ? c['disabled'] : null,
    customerKind: text(c['customer_kind']),
    country: text(c['country_code']),
    // ABSENT rather than 0 when the read failed. 0 is a real tier.
    tier: whole(c['verification_tier']),
    screen: text(c['screen']),
    errorCode: text(c['error_code']),
    intentKey: text(c['intent_key']),
    offeredArticles: stringList(c['offered_articles']),
    claimedRefs: stringList(c['claimed_refs']),
  }
}

interface Contact {
  readonly reference: string
  readonly customerId: number | null
  readonly funnelReference: string
  readonly entryRoute: string
  readonly lang: string
  readonly message: string
  readonly redactedAt: string
  readonly context: ContactContext
  readonly status: string
  readonly handledBy: string
  readonly handledAt: string
  readonly createdAt: string
}

function readContact(raw: unknown): Contact | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null
  const c = raw as Record<string, unknown>
  const reference = text(c['contact_reference'])
  if (reference === '') return null
  return {
    reference,
    customerId: whole(c['customer_id']),
    funnelReference: text(c['funnel_reference']),
    entryRoute: text(c['entry_route']),
    lang: text(c['lang']),
    message: text(c['message']),
    redactedAt: text(c['redacted_at']),
    context: readContext(c['context']),
    status: text(c['status']),
    handledBy: text(c['handled_by']),
    handledAt: text(c['handled_at']),
    createdAt: text(c['created_at']),
  }
}

/** `2026-07-31T10:02:00Z` → `2026-07-31 10:02 UTC`. */
function stamp(at: string): string {
  return at === '' ? '' : `${at.slice(0, 10)} ${at.slice(11, 16)} UTC`
}

function statusChip(c: Contact): ChipCell {
  if (c.status === 'open') return ['open', 'a']
  if (c.status === 'acknowledged') return ['picked up', 'n']
  if (c.status === 'resolved') return ['resolved', 'g']
  return [c.status === '' ? '— not stated' : `unknown: ${c.status}`, 'r']
}

/**
 * The entry route, in words rather than as a key.
 *
 * `guides_unavailable` is the alarming one and is toned as such: it is an
 * outage of the help centre, and letting it read like `no_guides_available`
 * would make a broken system look like a content gap for as long as it lasted.
 */
function routeCell(c: Contact): ChipCell {
  if (c.entryRoute === 'after_guides') return ['read the guides first', 'n']
  if (c.entryRoute === 'no_guides_available') return ['no guide for this', 'a']
  if (c.entryRoute === 'guides_unavailable') return ['GUIDES WERE DOWN', 'r']
  if (c.entryRoute === 'direct') return ['went straight to a person', 'n']
  return [c.entryRoute === '' ? '— not stated' : `unknown: ${c.entryRoute}`, 'r']
}

/**
 * The context cell.
 *
 * Three readings, and the middle one is why the flag exists: a context that
 * could not be read is not a thin context. An agent shown tier 0 for a customer
 * at tier 3 answers a different question from the one they were asked.
 */
function contextCell(c: Contact): ChipCell {
  if (c.context.unread) return ['COULD NOT BE READ', 'r']
  if (c.context.accountState !== '') return ['partial — customer row failed', 'a']
  if (c.context.intentKey === '' && c.context.screen === '') return ['thin — no screen or intent', 'a']
  return ['complete', 'g']
}

const COLS: readonly Column[] = [
  ['Contact', 'm', 'minmax(140px,1fr)'],
  ['Customer', 'm', '110px'],
  ['Opened', 'm', '150px'],
  ['How they got here', 'c', '190px'],
  ['Context', 'c', '190px'],
  ['Lang', 'm', '80px'],
  ['Status', 'c', '130px'],
]

function row(c: Contact): readonly Cell[] {
  return [
    c.reference,
    c.customerId === null ? '— not returned' : String(c.customerId),
    c.createdAt === '' ? '— not returned' : stamp(c.createdAt),
    routeCell(c),
    contextCell(c),
    c.lang === '' ? '—' : c.lang,
    statusChip(c),
  ]
}

function queueTable(rows: readonly Contact[], unreadable: number): TableBlock {
  const open = rows.filter((c) => c.status === 'open').length
  const down = rows.filter((c) => c.entryRoute === 'guides_unavailable').length
  const head = `${String(rows.length)} contact(s) · ${String(open)} waiting for a person`
  return {
    t: 'table',
    title: 'support_contacts · oldest first within a status',
    src: 'ledger',
    facets: false,
    cols: COLS,
    // Never re-sorted. The backend returns oldest-first within a status because
    // a support queue worked newest-first is one where the oldest request is
    // never reached.
    rows: rows.map(row),
    footer: `${head}${down === 0 ? '' : ` · ${String(down)} filed while the guides were down`}${unreadable === 0 ? '' : ` · ${String(unreadable)} row(s) could not be read`}`,
    foot2:
      'A contact moves forward only — picked up, then resolved, never reopened — so a customer who needs more help files another one. This console reads the queue and does not move anything: marking a request handled attributes a named agent to a customer’s request, and that is a separate permission.',
  }
}

/**
 * The context package for one contact, as the design's `sp-ticket` kv.
 *
 * Built for the OLDEST OPEN contact rather than an arbitrary one: that is the
 * row an agent picks up next, and a package drawn for a resolved ticket at the
 * top of a sorted list would be showing the work already done.
 */
function packageItems(c: Contact): readonly KvItem[] {
  const ctx = c.context
  if (ctx.unread) {
    return [
      [
        'context',
        'could not be read',
        'unread',
        'r',
        'The customer read FAILED as the ticket was filed, so every field of this package is absent rather than empty. Read this before anything else: verification tier 0 is a REAL tier — the one every new customer is in — so without this flag a failed read is indistinguishable from an unverified customer, and an agent would handle a verified business as an unverified one.',
        '',
      ],
      ['contact', c.reference, '', 'n', `filed ${stamp(c.createdAt)}`, 'm'],
      [
        'customer',
        c.customerId === null ? '— not returned' : String(c.customerId),
        '',
        'n',
        'Look them up before answering: nothing in this package can be relied on.',
        'm',
      ],
    ]
  }

  const items: KvItem[] = [
    ['contact', c.reference, '', 'n', `filed ${stamp(c.createdAt)}`, 'm'],
    [
      'customer',
      c.customerId === null ? '— not returned' : String(c.customerId),
      '',
      'n',
      `${ctx.customerKind === '' ? 'kind not stated' : ctx.customerKind}${ctx.country === '' ? '' : ` · ${ctx.country}`}${c.lang === '' ? '' : ` · replies resolve in ${c.lang}`}`,
      'm',
    ],
    [
      'onboarding acceptance',
      ctx.acceptance === '' ? '— not stated' : ctx.acceptance,
      ctx.acceptance === 'accepted' ? 'accepted' : ctx.acceptance === '' ? '' : ctx.acceptance,
      ctx.acceptance === 'accepted' ? 'g' : ctx.acceptance === 'rejected' ? 'r' : 'a',
      'The ONBOARDING acceptance state and nothing else. It carries nothing about disablement or any later restriction — the row below carries that half.',
      '',
    ],
    [
      'account disabled',
      ctx.disabled === null ? '— not stated' : ctx.disabled ? 'yes' : 'no',
      ctx.disabled === true ? 'disabled' : '',
      ctx.disabled === true ? 'r' : 'n',
      'Separate from acceptance, because an accepted customer can be disabled and a console that showed one number would answer the wrong question.',
      '',
    ],
    [
      'verification tier',
      // 0 is a real tier and renders as 0. Absence renders as absence.
      ctx.tier === null ? '— not returned' : String(ctx.tier),
      '',
      'n',
      ctx.tier === 0
        ? 'Tier 0 is a real tier — the one every new customer is in — and this is a read that succeeded.'
        : 'As read when the ticket was filed.',
      '',
    ],
    [
      'where they were',
      ctx.screen === '' ? '— not stated' : ctx.screen,
      '',
      'n',
      `${ctx.errorCode === '' ? 'no error code' : `error ${ctx.errorCode}`}${ctx.intentKey === '' ? '' : ` · intent ${ctx.intentKey}`}`,
      'm',
    ],
    [
      'guides already offered',
      ctx.offeredArticles.length === 0 ? '— none' : ctx.offeredArticles.join(', '),
      '',
      'n',
      'What the funnel showed them before they asked for a person. Answering with one of these again is the commonest way a first reply wastes everybody’s time.',
      '',
    ],
    [
      'references they attached',
      ctx.claimedRefs.length === 0 ? '— none' : ctx.claimedRefs.join(', '),
      ctx.claimedRefs.length === 0 ? '' : 'CLAIMED',
      ctx.claimedRefs.length === 0 ? 'n' : 'a',
      'CLAIMS, not resolved records. They are shape-checked and capped, and nothing verifies that one names a movement — or a movement belonging to this customer. Look one up under this ticket’s own customer before discussing it.',
      'm',
    ],
    [
      'card data',
      'none',
      'n/a',
      'n',
      'No PAN, no last four, nothing card-shaped exists in this package or in the payload behind it.',
      'b',
    ],
  ]

  if (c.redactedAt !== '') {
    items.unshift([
      'the customer’s words',
      'erased',
      'Art. 17',
      'a',
      `An erasure took the message on ${stamp(c.redactedAt)}. What remains in its place is a tombstone WE wrote — reading it as something the customer said would mean answering something nobody asked.`,
      '',
    ])
  } else if (c.message !== '') {
    items.unshift(['what they said', c.message, '', 'n', 'As the customer wrote it.', ''])
  }

  return items
}

function packageBlock(rows: readonly Contact[]): Block | null {
  // The oldest OPEN contact — the row an agent picks up next. A package drawn
  // for a resolved ticket would be showing work already done.
  const next = rows.find((c) => c.status === 'open') ?? rows[0]
  if (next === undefined) return null
  return {
    t: 'kv',
    title: `Package · ${next.reference}${next.status === 'open' ? ' (next to pick up)' : ''}`,
    items: packageItems(next),
  }
}

function emptyNote(): Block {
  return {
    t: 'note',
    tone: 'g',
    kicker: 'Support',
    title: 'Nobody is waiting for a person',
    body: 'Context packages are generated when a customer opens support from inside the app. An empty queue means none is outstanding — and because this read covers every status rather than open only, it also means none has been filed in what the queue holds.',
    ref: 'support_contacts · the read returned no rows',
  }
}

function guidesDownNote(count: number): Block {
  return {
    t: 'note',
    tone: 'r',
    kicker: 'Not a content gap',
    title: `${String(count)} contact(s) were filed because the guides would not load`,
    body: 'That is an outage of the help centre, not a customer the funnel had nothing for. The two are separate entry routes precisely so a broken system is not counted as a content gap for as long as it lasts — and so the deflection numbers do not quietly absorb it.',
    ref: 'entry_route = guides_unavailable',
  }
}

function unreadableRowsNote(count: number): Block {
  return {
    t: 'note',
    tone: 'r',
    kicker: 'Partial read',
    title: `${String(count)} contact(s) could not be read`,
    body: 'Counted rather than dropped. A queue quietly shorter than the record is a customer who was told somebody would look and nobody did — which is the exact failure this screen exists to prevent.',
    ref: 'a contact with no reference cannot be picked up or resolved and is refused',
  }
}

/**
 * Why picking a contact up is its own permission, said once for the screen.
 *
 * Reading the queue and working it are different acts, and the backend splits
 * them deliberately: *"it must be possible to give somebody the queue without
 * giving them the ability to mark a request handled."* An agent who can see
 * every control below disabled is not being denied the screen — they have the
 * screen, and not the second permission.
 */
const WORKING_IS_SEPARATE: Block = {
  t: 'note',
  tone: 'n',
  quiet: true,
      kicker: 'Two permissions',
  title: 'Reading this queue and working it are separate permissions',
  body: 'Picking a contact up attributes a named agent to a customer’s request, so it is granted separately from being able to read the queue at all. If the controls below are disabled for you, you have the queue and not the second permission — which is a deliberate arrangement rather than a gap. Moves are forward only: picked up, then resolved, never reopened.',
  ref: 'support.contact.read is the queue · support.contact.work is picking one up',
}

/** The client and the caller, or `null` on a surface that only reads. */
export interface Writer {
  readonly api: ApiClient
  readonly caller: Caller
}

export function composeSupportContacts(
  res: SupportContactsResponse, readAt: Date, writer: Writer | null = null,
): ScreenData {
  const rows: Contact[] = []
  let unreadable = 0
  for (const raw of res.contacts) {
    const parsed = readContact(raw)
    if (parsed === null) unreadable += 1
    else rows.push(parsed)
  }

  const blocks: Block[] = [queueTable(rows, unreadable)]

  const pkg = packageBlock(rows)
  if (pkg !== null) blocks.push(pkg)

  const down = rows.filter((c) => c.entryRoute === 'guides_unavailable').length
  if (down > 0) blocks.push(guidesDownNote(down))
  if (unreadable > 0) blocks.push(unreadableRowsNote(unreadable))
  if (rows.length === 0 && unreadable === 0) blocks.push(emptyNote())
  blocks.push(WORKING_IS_SEPARATE)

  // One control per contact rather than one taking a reference, because the
  // moves available depend on THIS contact's state and a typed reference would
  // let somebody resolve a ticket that is not on their screen.
  if (writer !== null) {
    for (const c of rows) {
      blocks.push(contactStatusForm(writer.api, writer.caller, {
        reference: c.reference, status: c.status, handledBy: c.handledBy,
      }))
    }
  }

  const unreadContext = rows.filter((c) => c.context.unread).length
  const missing =
    unreadContext === 0
      ? []
      : [`the context package for ${String(unreadContext)} contact(s) — the customer read failed as the ticket was filed`]

  const provenance: DataSource =
    missing.length === 0
      ? { kind: 'live', source: SOURCE, authority: AUTHORITY, readAt: readAtStamp(readAt) }
      : { kind: 'partial', source: SOURCE, authority: AUTHORITY, readAt: readAtStamp(readAt), missing }

  return { blocks, provenance, rowKeys: rows.map((c) => c.reference) }
}

function unreadableScreen(reason: string, decisionRef: string | null): ScreenData {
  return {
    blocks: [{
      t: 'note',
      tone: 'r',
      kicker: 'Support',
      title: 'The contact queue could not be read',
      body: `${reason} Nobody on this screen is known — and an unread queue is not an empty one. Every customer who filed a request was told a person would look at it, and that promise is outstanding whether or not this screen can see them.`,
      ref: decisionRef === null
        ? 'No contact was read. This is not an empty queue.'
        : `decision_ref ${decisionRef}`,
    }],
    provenance: { kind: 'unread', source: SOURCE, reason },
  }
}

function isContactsResponse(payload: unknown): payload is SupportContactsResponse {
  if (typeof payload !== 'object' || payload === null) return false
  return Array.isArray((payload as Partial<SupportContactsResponse>).contacts)
}

export const loadSupportContacts: ScreenLoader = async (api) => {
  const calling = callerBeside(api)
  let payload: unknown
  try {
    // No `status` filter: the endpoint says an open-only default hides the
    // queue's history, and an agent picking work up needs to see that the last
    // three tickets from this customer were resolved by somebody else.
    payload = await api.get<unknown>(SUPPORT_CONTACTS_PATH)
  } catch (error) {
    if (error instanceof ApiError) {
      return unreadableScreen(
        `The contacts endpoint answered ${String(error.status)}${error.code === null ? '' : ` (${error.code})`}.`,
        error.decisionRef,
      )
    }
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    return unreadableScreen('The backend could not be reached.', null)
  }

  if (!isContactsResponse(payload)) {
    return unreadableScreen('The contacts endpoint returned a shape this console does not know.', null)
  }
  return composeSupportContacts(payload, new Date(), { api, caller: await calling })
}
